import { clearTree, requestTreeFiltersToogle } from 'actions';
import { enterEditWidgetMode } from 'actions/dashboardEdit';
import Permissions from 'components/Permissions';
import Tooltip from 'components/Tooltip';
import { NEW_EDIT_ICON } from 'constants/commonIcons';
import { allowedReadOnlyDimensions, metricsLinksMap } from 'constants/dashboard';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import withWidget from 'decorators/widget';
import WidgetEmptyState from 'decorators/widget/WidgetEmptyState';
import WidgetError from 'decorators/widget/WidgetError';
import WidgetLoading from 'decorators/widget/WidgetLoading';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getCustomerDomain, getUser } from 'selectors/auth';
import { getCustomerDashboardConfig, getTree } from 'selectors/dashboard';
import { getIsEditingWidget } from 'selectors/dashboardEdit';
import { getNavParamsByResourceMemo, getNavWidgetFiltersMemo } from 'selectors/nav';
import { getPermissionsPolicy } from 'selectors/permissions';
import { useSelector } from 'store';
import { DashboardMetric, DefaultWidgetComponentProps, MetricsDataV2 } from 'types';
import logEvent from 'utils/analytics';
import { buildPeriodTrendDataFromMetrics, joinQueryFilters } from 'utils/dashboard';
import { getValuesFromDimensions, transformToString } from 'utils/parserTree';
import { isActionAuthorized } from 'utils/permissions';
import { useHistory } from 'utils/urls';

export const NiceListWidget: React.FC<
  DefaultWidgetComponentProps & { widgetData: MetricsDataV2 }
> = (props) => {
  const { widgetData, queryData, widgetId, title } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const { activeUuid } = useParams<{ activeUuid: string }>();

  const user = useSelector(getUser);
  const policy = useSelector(getPermissionsPolicy);
  const customerDomain = useSelector(getCustomerDomain);
  const widgetFilters = useSelector((state) => getNavWidgetFiltersMemo(state, widgetId));
  const filters = useSelector((state) =>
    getNavParamsByResourceMemo(state, resourceQueryParamName.metrics)
  );
  const urlTree = useSelector(getTree);

  const dashboardWidgets = useSelector((state) =>
    getCustomerDashboardConfig(state, activeUuid)
  ).widgets;
  const editMode = useSelector(getIsEditingWidget);

  if (widgetData == null || queryData == null) {
    return <WidgetLoading />;
  }

  const processedData = buildPeriodTrendDataFromMetrics(widgetData, true);

  const handleEditWidget = (): void => {
    const widgetIndex = Number.parseInt(widgetId.split('--')[3], 10);
    const widgetConfig = dashboardWidgets[widgetIndex];
    dispatch(enterEditWidgetMode({ widget: widgetConfig, widgetIndex }));
  };

  if ('error' in processedData && processedData.error != null) {
    return <WidgetError msg={processedData.error} />;
  }

  if ('data' in processedData && processedData.data.length === 0) {
    return (
      <WidgetEmptyState
        widgetTitle={title}
        editMode={editMode}
        handleEditWidget={handleEditWidget}
      />
    );
  }

  const handleClick = (query: DashboardMetric, data?: { id: string; timestamp: string }): void => {
    const [id, type] = widgetId.split('--');
    logEvent(`Dashboard-click-${id}-${type}`);
    const resourceId = data?.id;

    if (query == null) {
      return;
    }

    const info = metricsLinksMap[query.metric];
    if (info == null || !isActionAuthorized(info.action, user.roles, policy)) {
      return;
    }

    const dimensions: Record<string, string | string[]> = {};
    const infoDimension = info.dimensions;
    if (infoDimension != null) {
      // Top level dimensions
      Object.entries(infoDimension).forEach(([dimension, param]) => {
        const allowed = allowedReadOnlyDimensions[query.metric];

        // if allowedDimension is not defined or the dimension is not included,
        // just ignore it
        if (allowed == null || !allowed.includes(dimension)) {
          return;
        }

        let value: string | string[] = '';
        let infoParam;

        if (Array.isArray(query.group_by)) {
          infoParam = infoDimension[query.group_by[query.group_by.length - 1]];
        } else {
          infoParam = query.group_by && infoDimension[query.group_by];
        }

        // add the resource uuid whose dimension was used as group by
        if (query.group_by && infoParam === param && resourceId) {
          value = resourceId;
        }

        if (filters[dimension] != null) {
          const topValue = filters[dimension];
          value = topValue;
        }

        if (widgetFilters[dimension] != null) {
          const widgetValue = filters[dimension];
          value = widgetValue;
        }

        dimensions[`${info.resource}__${param}`] = value;
      });

      // Metric level dimensions
      if (query.dimensions != null) {
        query.dimensions.forEach((dimension) => {
          const allowed = allowedReadOnlyDimensions[query.metric];

          // if allowedDimension is not defined or the dimension is not included,
          // just ignore it
          if (allowed == null || !allowed.includes(dimension.name)) {
            return;
          }

          if (!dimensions[`${info.resource}__${infoDimension[dimension.name]}`]) {
            dimensions[`${info.resource}__${infoDimension[dimension.name]}`] = dimension.value;
          }
        });
      }
    }

    // Metric level dimensions have more priority than top leve dimensions
    // multiple query.period by n for trailing avg

    // @ts-ignore
    const widgetFilterSearch = query.filters_search || '';

    const tree = getValuesFromDimensions(dimensions);

    let searchString = transformToString(tree);
    if (typeof widgetFilterSearch === 'string') {
      searchString = joinQueryFilters(searchString, widgetFilterSearch);
    }
    searchString = joinQueryFilters(searchString, transformToString(urlTree));

    dispatch(requestTreeFiltersToogle({ value: true }));
    dispatch(clearTree());
    history.pushLookup({
      customerDomain,
      routeName: info.route,
      queryParams: {
        [`${info.resource}__filters_search`]: searchString,
        [`${info.resource}__has_events`]: ['true', 'false'],
        ...dimensions,
      },
    });
  };

  return (
    <div className="h-full w-full flex items-start pt-2.5">
      <div
        className="grid gap-1 w-full text-menu-item"
        style={{
          gridTemplateRows: 'auto 1fr',
          gridTemplateColumns: '0.1fr 1fr 0.3fr',
        }}
      >
        <div className="col-span-2 flex flex-row gap-1">
          <span className="">{title}</span>

          {!editMode && (
            <Permissions action="analytics.editWidget" key="edit-button">
              <Tooltip tooltip="dashboard.editWidget">
                <button type="button" className="focus:outline-none w-3" onClick={handleEditWidget}>
                  {NEW_EDIT_ICON('text-litlingo-gray-6')}
                </button>
              </Tooltip>
            </Permissions>
          )}
        </div>
        <span>Count</span>
        <div className="flex flex-col bg-white rounded overflow-hidden col-start-1 row-start-2 col-span-4">
          {'data' in processedData &&
            processedData.data.map((data, idx) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <div
                className={`grid ${
                  idx !== 0 ? 'border-t border-litlingo-gray-3' : ''
                } hover:bg-litlingo-gray-0.5`}
                style={{
                  gridTemplateColumns: '0.1fr 1fr 0.3fr',
                }}
                // eslint-disable-next-line react/no-array-index-key
                key={`${data.label}--${idx}`}
                // @ts-ignore
                onClick={(): void => handleClick(queryData.queries[0], data)}
                role="button"
                tabIndex={0}
              >
                <div className="flex flex-row justify-center items-center min-w-10 text-heading-3 bg-litlingo-secondary-80">
                  <svg width={4} height={4} viewBox="0 0 4 4" fill="black">
                    <circle cx={2} cy={2} r={2} />
                  </svg>
                </div>

                <div
                  className="flex-1 flex items-center px-2 py-2 text-body truncate cursor-pointer"
                  aria-hidden
                >
                  <div className="truncate">{data.label}</div>
                </div>

                <div className="flex items-center text-body cursor-pointer" aria-hidden>
                  {data.count}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default withWidget({
  fetchAvgPeriod: true,
  bgClassColor: 'bg-transparent',
  noHeader: true,
})(NiceListWidget as React.FC<DefaultWidgetComponentProps>);
